import React, {
  useMemo,
  useState,
  useEffect,
  useReducer,
  useContext,
  useRef,
} from 'react'
import { ProjectContext, withListener } from 'contexts/ProjectContext'
import {
  Routes,
  Route,
  Link,
  useParams,
  useNavigate,
  useMatch,
  useLocation,
} from 'react-router-dom'
import { getVoicesUrl, getVoiceUrl } from 'data/voices/list'
import { ContactCard } from 'components/ContactInfo'
import useBreakpoint from 'hooks/useBreakpoint'
import MarkdownCopy from 'containers/MarkdownCopy'
import { reverseLookupMessage } from 'messageUtils'
import languageNames from 'data/languages/messages'
import ExplorePage from 'containers/ExplorePage/Loadable'
import useApi from 'hooks/useApi'
import { transformVoices } from 'utils'
import useCurrentUser from 'hooks/useCurrentUser'
import Page from 'containers/Page'
import VoicePage from 'components/VoicePage'
import AiPage from 'components/AiPage'
import VoiceDrawer from 'components/VoiceDrawer'
import AiVoiceSuggestDrawer from 'components/AiVoiceSuggestDrawer'
import languages from 'data/languages/list'

const ExploreRoutes = () => {
  const lastCurrentPage = useRef()
  const location = useLocation()
  const params = useParams()
  const match = useMatch(location.pathname)
  const { project, dispatch } = useContext(ProjectContext)
  const [voices, setVoices] = useState([])
  const [aiDrawerState, setAiDrawerState] = useState({
    isExpanded: false,
  })
  const [characterMap, setCharacterMap] = useState({})
  const [rawData, loading, errors, refetch, meta] = useApi(
    getVoicesUrl(project, project.order),
    { load: false }
  )

  const data = useMemo(() => transformVoices(rawData), [rawData])

  const currentUser = useCurrentUser()

  useEffect(() => {
    const nextProject = { ...project }

    if (project.currentPage > 1) {
      nextProject.currentPage = 1
      dispatch({ type: 'SET', payload: { key: 'currentPage', value: 1, } })
    }

    setVoices([])
    refetch(getVoicesUrl(nextProject, project.order))
  }, [
    project.languages,
    project.languagesProficiency,
    project.demoLanguages,
    project.demoDialects,
    project.demoAccents,
    project.categories,
    project.mediums,
    project.genders,
    project.tags,
    project.query,
    project.voiceages,
    project.availableFrom,
    project.availableTo,
    project.collections,
    project.newSince,
  ])

  useEffect(() => {
    setAiDrawerState({ isExpanded: false })
  }, [project.voices])

  useEffect(() => {
    refetch(getVoicesUrl(project, project.order))
  }, [project.currentPage])

  useEffect(() => {
    if (!loading && data) {

      lastCurrentPage.current = project.currentPage

      if (lastCurrentPage.current == 1) {
        setVoices(data);
      } else {
        setVoices((prevVoices) => {
          return [...prevVoices, ...data]
            .filter(({ sku }, i, arr) => !!arr.find(({ sku: innerSku }) => innerSku == sku))
        })
      }
    }
  }, [data, loading])

  const onFetchVoice = (sku) => {
    refetch(getVoiceUrl(sku))
  }

  return (
    <div className="w-full h-full md:flex">
      <Page
        nav={!currentUser?.roles?.includes('coordinator')}
        navProps={{ fixed: false }}
        childrenProps={{ className: 'h-full' }}
        className={`${
          project.isScoped || aiDrawerState.isExpanded ? 'md:w-3/4' : 'w-full'
        } transition-[width] ease-in-out delay-150`}
        footer={false}
      >
        <React.Fragment>
          <Routes>
            <Route
              index
              element={
                <ExplorePage
                  project={project}
                  dispatch={dispatch}
                  meta={meta}
                  voices={voices}
                  loading={loading}
                  loadingMore={loading && lastCurrentPage.current != project.currentPage}
                  language={params.language}
                  gender={params.gender}
                  currentUser={currentUser}
                  characterMap={characterMap}
                  setCharacterMap={setCharacterMap}
                  onSuggestWithAI={() => {
                    setAiDrawerState({
                      isExpanded: true,
                    })
                  }}
                />
              }
            />

            <Route
              path="ai"
              element={ <AiPage project={project}/> }
            />

            <Route
              path="ai/:voiceId"
              element={ <AiPage project={project}/> }
            />

            <Route
              path=":voiceId"
              element={
                <VoicePage
                  project={project}
                  dispatch={dispatch}
                  voices={voices}
                  loading={loading}
                  onFetchVoice={onFetchVoice}
                />
              }
            />
          </Routes>
        </React.Fragment>
      </Page>

      <VoiceDrawer project={project} dispatch={dispatch} />
      <AiVoiceSuggestDrawer
        isExpanded={aiDrawerState.isExpanded}
        onSuggestion={(suggestion) => {
          let tags = [];
          let categories = [];

          if (suggestion.tone) {
            tags.push(suggestion.tone)
          }

          if (suggestion.category) {
            categories.push(suggestion.category)
          }

          let payload = {
            tags,
            categories,
          }

          dispatch({ type: 'PATCH', payload })
        }}
        onClose={() => {
          setAiDrawerState({
            isExpanded: false,
          })
        }}
      ></AiVoiceSuggestDrawer>
    </div>
  )
}

export default withListener(ExploreRoutes)
